import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ApiFragment, BaseContainer, Family, KeyFrame, KeyFramesService, PreviewStatus } from '@stylehaven/components';
import { FragmentPlaybackPlugin } from '../plugins/fragment-playback.plugin';
import { SubtitlesPlugin } from '../plugins/subtitles.plugin';
import { ThumbnailsPlugin } from '../plugins/thumbnails.plugin';

declare let videojs: any;

@Component({
    selector: 'med-media-player',
    templateUrl: './media-player.component.html',
    styleUrls: ['./media-player.component.scss'], // TODO see if we can include the videojs css here instead of globally
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MediaPlayerComponent extends BaseContainer implements OnInit, AfterViewInit {
    private readonly FPS: number = 25;

    private player: any;
    private offset = 0;
    private subtitlesPlugin: SubtitlesPlugin;
    private thumbnailsPlugin: ThumbnailsPlugin;
    private fragmentPlaybackPlugin: FragmentPlaybackPlugin;

    private mediaKeyFrames$: Observable<KeyFrame[]>;
    private viewInit$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    public mediaRecord$: ReplaySubject<ApiFragment> = new ReplaySubject<ApiFragment>(1);
    public previewStatus$: BehaviorSubject<PreviewStatus | null> = new BehaviorSubject<PreviewStatus | null>(null);

    public placeholderMessage = '';
    private navigationWithAutoplay = false;

    @ViewChild('player', { static: false }) videoElement?: ElementRef;

    @Input() set record(record: ApiFragment) {
        const offset: number | null = record.get(Family.Technical, 'StartFrames');
        this.offset = offset ? offset : 0;

        this.mediaRecord$.next(record);
    }

    constructor(
        private keyFrameService: KeyFramesService,
        private readonly activatedRoute: ActivatedRoute
    ) {
        super();
    }

    ngOnInit(): void {
        this.placeholderMessage = this.translateService.translate('ui.loading');
        this.mediaRecord$.pipe(takeUntil(this.destroy$)).subscribe(record => this.initPreviewStatus(record));
        const autoplay: string = this.activatedRoute.snapshot.queryParamMap.get('autoplay') ?? 'false';
        this.navigationWithAutoplay = Boolean(JSON.parse(autoplay));
    }

    ngAfterViewInit(): void {
        this.viewInit$.next(true);
    }

    private initPreviewStatus(record: ApiFragment): void {
        if (record.hasIncompleteBrowse() || record.doesNotHaveBrowse()) {
            this.placeholderMessage = this.translateService.translate('overrides.RecordStatus.Rejected');
            this.previewStatus$.next(PreviewStatus.Error);
        } else if (record.isProcessing() || record.isBrowseProcessing()) {
            this.placeholderMessage = this.translateService.translate('overrides.RecordStatus.Processing');
            this.previewStatus$.next(PreviewStatus.Processing);
        } else {
            this.initKeyframes();
            combineLatest([this.mediaRecord$, this.mediaKeyFrames$, this.viewInit$, this.previewStatus$])
                .pipe(takeUntil(this.destroy$))
                .subscribe(([mediaRecord, keyframes, viewInit, status]) => {
                    if (status === PreviewStatus.Ready) {
                        if (this.videoElement) {
                            this.onReady(mediaRecord, keyframes);
                        }
                    }
                });
        }
    }

    private onReady(mediaRecord: ApiFragment, mediaKeyFrames: KeyFrame[]): void {
        this.initPlayer(mediaRecord);
        this.initPlayerFeatures(mediaRecord, mediaKeyFrames);
        this.setPoster(mediaRecord);
    }

    private initKeyframes(): void {
        this.mediaKeyFrames$ = this.mediaRecord$.pipe(
            switchMap(record => this.keyFrameService.getKeyFrames(record.getFragmentId())),
            catchError(e => {
                console.warn(e);
                return of([]);
            }),
            tap(() => {
                this.previewStatus$.next(PreviewStatus.Ready);
            }),
            takeUntil(this.destroy$)
        );
    }

    private initPlayer(mediaRecord: ApiFragment): void {
        const playerOptions: any = {
            controls: true,
            playbackRates: [0.5, 1, 1.25, 1.5, 2, 4, 8],
            controlBar: {
                remainingTimeDisplay: false,
                currentTimeDisplay: true,
                durationDisplay: true,
                mouseTimeDisplay: false,
                pictureInPictureToggle: false,
                captionsButton: true
            },
            autoplay: this.navigationWithAutoplay,
            preload: 'auto',
            techOrder: ['html5']
        };

        this.player = new videojs(this.videoElement?.nativeElement, playerOptions);

        // Note that audio media is also converted to mp4 by the engine
        this.player.src({ type: 'video/mp4', src: mediaRecord.getMediaUrl() });
    }

    private initPlayerFeatures(mediaRecord: ApiFragment, mediaKeyFrames: KeyFrame[]): void {
        const subtitleUrl: string | null = mediaRecord.getSubtitleUrl();

        this.initTimeFormatConversion();

        if (subtitleUrl) {
            this.initSubtitle(subtitleUrl);
        }

        if (mediaKeyFrames) {
            this.initThumbnailsOnHover(mediaKeyFrames);
        }

        if (!mediaRecord.isMainFragment()) {
            this.initFragmentPlayback(mediaRecord);
        }
    }

    private initTimeFormatConversion(): void {
        videojs.setFormatTime(this.toTimeFormat.bind(this));
    }

    private toTimeFormat(time: number): string {
        if (Number.isNaN(time)) {
            time = 0;
        }

        time += this.offset / this.FPS;
        let timestring: string = new Date(1000 * time).toISOString().substr(11, 8);
        const frame: number = (time * this.FPS) % this.FPS;

        if (frame >= 10) {
            timestring += ':' + frame.toString().substr(0, 2);
        } else {
            timestring += ':0' + frame.toString().substr(0, 1);
        }
        return timestring;
    }

    private initSubtitle(subtitleUrl: string): void {
        this.subtitlesPlugin = new SubtitlesPlugin(this.player, subtitleUrl);
        this.subtitlesPlugin.initPlugin();
    }

    private initThumbnailsOnHover(mediaKeyFrames: KeyFrame[]): void {
        this.thumbnailsPlugin = new ThumbnailsPlugin(this.player, mediaKeyFrames, this.offset);
        this.thumbnailsPlugin.initPlugin();
    }

    private initFragmentPlayback(record: ApiFragment): void {
        this.fragmentPlaybackPlugin = new FragmentPlaybackPlugin(this.player, record);
        this.fragmentPlaybackPlugin.initPlugin();
    }

    private setPoster(mediaRecord: ApiFragment): void {
        // Remove previous eventlisteners from 'loadedmetadata'
        this.player.off('loadedmetadata');

        this.player.on('loadedmetadata', () => {
            this.player.poster(mediaRecord.getThumbnailUrl());
        });
    }

    public get spinning$(): Observable<boolean> {
        return this.previewStatus$.pipe(map(status => status === PreviewStatus.Processing));
    }
}
